import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PlusCircle, User, LogOut, Palette, Coins, Menu, X } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Fetch user credits
  useEffect(() => {
    const fetchCredits = async () => {
      if (!isAuthenticated) return;

      try {
        setLoadingCredits(true);
        const token = localStorage.getItem('access_token'); // Changed from 'token' to 'access_token'
        if (!token) {
          console.log('No token found');
          return;
        }

        console.log('Fetching credits from:', `${API_BASE_URL}/ai/credits/balance/`);
        const response = await axios.get(
          `${API_BASE_URL}/ai/credits/balance/`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            }
          }
        );

        console.log('Credit API Response:', response.data);

        if (response.data.success) {
          const availableCredits = response.data.balance.available_credits;
          console.log('Setting credits to:', availableCredits);
          setCredits(availableCredits);
        } else {
          console.error('API returned success: false');
        }
      } catch (error) {
        console.error('Error fetching credits:', error);
        console.error('Error response:', error.response?.data);
      } finally {
        setLoadingCredits(false);
      }
    };

    fetchCredits();
    
    // Refresh credits every 30 seconds
    const interval = setInterval(fetchCredits, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/logo.png" 
              alt="NovialCraft" 
              className="h-8 sm:h-10 w-auto"
            />
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/pricing" 
              className="text-gray-600 hover:text-gray-900 transition-colors"
            >
              Pricing
            </Link>
            
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                >
                  My Carousels
                </Link>
                
                {/* Credit Display */}
                <Link
                  to="/pricing"
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <Coins className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-600">
                    {loadingCredits ? '...' : credits !== null ? credits : '0'} credits
                  </span>
                </Link>
                
                <Link 
                  to="/create" 
                  className="btn-primary flex items-center space-x-2"
                >
                  <PlusCircle className="h-4 w-4" />
                  <span>Create New</span>
                </Link>
              </>
            ) : (
              <Link 
                to="/login" 
                className="btn-primary"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* User Menu */}
          {isAuthenticated && (
            <div className="relative group">
              <button className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
                <User className="h-5 w-5" />
                <span className="hidden md:block">{user?.email}</span>
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-1">
                  <Link 
                    to="/dashboard" 
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Dashboard
                  </Link>
                  <button 
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button 
              onClick={toggleMobileMenu}
              className="text-gray-600 hover:text-gray-900 p-2"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/pricing"
                onClick={closeMobileMenu}
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              >
                Pricing
              </Link>

              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  >
                    My Carousels
                  </Link>

                  {/* Credits Display Mobile */}
                  <Link
                    to="/pricing"
                    onClick={closeMobileMenu}
                    className="flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium bg-purple-50 text-purple-600"
                  >
                    <Coins className="h-5 w-5" />
                    <span>{loadingCredits ? '...' : credits !== null ? credits : '0'} credits</span>
                  </Link>

                  <Link
                    to="/create"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-md text-base font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Create New
                  </Link>

                  {/* User Info */}
                  <div className="border-t border-gray-200 pt-4 pb-3">
                    <div className="flex items-center px-3">
                      <User className="h-8 w-8 text-gray-400" />
                      <div className="ml-3">
                        <div className="text-base font-medium text-gray-800">{user?.email}</div>
                      </div>
                    </div>
                    <div className="mt-3 space-y-1">
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                      >
                        <LogOut className="h-5 w-5 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-md text-base font-medium text-primary-600 hover:text-primary-700 hover:bg-gray-50"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 mt-2 rounded-md text-base font-medium text-center text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
